/**
 * API retry utility with exponential backoff
 * Helps prevent blank screens by retrying failed API requests
 */

export interface RetryOptions {
  maxRetries?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  onRetry?: (attempt: number, error: Error) => void;
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  initialDelayMs: 1000, // 1 second
  maxDelayMs: 8000, // 8 seconds max
  onRetry: () => {}, // no-op
};

type StatusCarrier = {
  status?: unknown;
  response?: { status?: unknown };
  cause?: unknown;
};

function statusOf(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const err = error as StatusCarrier;
  const status = err.response?.status ?? err.status;
  if (typeof status === 'number') return status;
  return err.cause && err.cause !== error ? statusOf(err.cause) : undefined;
}

/**
 * A definite client error: a numeric HTTP 4xx status (other than 408 Request
 * Timeout and 429 Too Many Requests) on `error.response.status` or
 * `error.status` (or on the error's `cause`). Retrying it cannot succeed, so
 * executeWithRetry fails fast. Conservative: an error WITHOUT status info
 * (network failure, timeout, a thrown plain Error) is not a client error.
 */
export function isClientError(error: unknown): boolean {
  const status = statusOf(error);
  return (
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    status !== 408 &&
    status !== 429
  );
}

/**
 * Execute an async function with exponential backoff retry logic.
 * Client errors (see isClientError, e.g. 403/404) are thrown after the first
 * attempt; network errors, timeouts, 408/429, 5xx and errors without a status
 * are retried.
 *
 * @param fn - Async function to execute
 * @param options - Retry configuration
 * @returns Promise that resolves with function result or rejects with last error
 *
 * @example
 * ```typescript
 * const data = await executeWithRetry(
 *   async () => fetchStepData(stepId),
 *   {
 *     maxRetries: 3,
 *     onRetry: (attempt, error) => {
 *       console.warn(`Retry attempt ${attempt}:`, error);
 *     }
 *   }
 * );
 * ```
 */
export async function executeWithRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const config = { ...DEFAULT_OPTIONS, ...options };
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < config.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      // A 4xx will not change on retry: surface it now (P4-3).
      if (isClientError(error)) throw lastError;

      // Don't retry on last attempt
      if (attempt < config.maxRetries - 1) {
        // Calculate delay with exponential backoff: 1s, 2s, 4s, 8s (max)
        const delay = Math.min(
          config.initialDelayMs * Math.pow(2, attempt),
          config.maxDelayMs,
        );

        config.onRetry(attempt + 1, lastError);

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  // All retries failed, throw last error
  throw lastError || new Error('All retry attempts failed');
}

/**
 * Check if an error is retryable (network/timeout errors)
 *
 * @param error - Error to check
 * @returns true if error should be retried
 */
export function isRetryableError(error: unknown): boolean {
  if (!error) return false;

  const err = error as Error & {
    code?: string;
    response?: { status?: number };
  };

  // Network errors
  if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
    return true;
  }

  // HTTP status codes that should be retried
  const retryableStatuses = [408, 429, 500, 502, 503, 504];
  if (err.response?.status && retryableStatuses.includes(err.response.status)) {
    return true;
  }

  // Network connection errors
  if (
    err.message?.includes('Network Error') ||
    err.message?.includes('timeout')
  ) {
    return true;
  }

  return false;
}
